'use client';

import { useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { getLocaleCode } from '@/helpers/locales';
import { cn } from '@/helpers/utils';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import Flag from './Flag';
import SvgChevronDown from './svg/SvgChevronDown';

export default function LanguageSwitcher() {
    const locale = useLocale();
    const pathname = usePathname();
    const ref = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    useOnClickOutside(ref, () => setOpen(false));

    return (
        <div ref={ref} className="group min-w-[140px] relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-haspopup="true"
                className="w-full inline-flex items-center justify-between gap-3 mouse-hover:bg-purple-800 mouse-hover:text-white pt-9 pb-[19px] px-6 mb-4 rounded-bl-[15px] rounded-br-[15px] font-medium transition-colors duration-200"
            >
                <span className="flex-1 flex items-center justify-start gap-3">
                    <Flag country={locale} className="shrink-0" />
                    {getLocaleCode(locale)}
                </span>
                <span className="relative top-px">
                    <SvgChevronDown
                        width={10}
                        height={6}
                        className={cn(
                            'transform transition-transform duration-200',
                            open ? 'rotate-180' : 'group-hover:rotate-180'
                        )}
                    />
                </span>
            </button>

            <div
                className={cn(
                    'absolute top-full right-0 pt-6',
                    open ? 'block' : 'hidden group-hover:block'
                )}
            >
                <div className="w-screen max-w-[120px] bg-white flex flex-col rounded-[15px] px-4 shadow-submenu">
                    {routing.locales.map((lang) => {
                        if (lang === locale) return null;
                        return (
                            <Link
                                href={pathname}
                                locale={lang}
                                key={lang}
                                onClick={() => setOpen(false)}
                                className="text-black/60 mouse-hover:text-black flex gap-3 py-4 border-b last:border-0 items-center transition-colors duration-200 submenu-item"
                            >
                                <Flag country={lang} className="shrink-0" />
                                <p>{getLocaleCode(lang)}</p>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
